import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Kpi, Panneau, Squelette, Vide, ClientTel } from '../components/ui';
import { dateFr, heureFr, montant, montantCourt, nomClient } from '../lib/format';
import type { Reservation, Tache, Tresorerie, LigneCapacite } from '../api/types';
import { T } from '../i18n';

interface Reponse {
  date: string;
  taches: Tache[];
  encaissementsEnAttente: { _id: string; numero: string; soldeMinor: number; customer?: unknown; reservation?: { numero: string } }[];
  correctionsEnAttente: { _id: string; champ: string; libelleEntite: string; motif: string; createdAt: string; demandeur?: { nom: string } }[];
  alertes: { niveau: string; texte: string }[];
  resume: { departs: number; retours: number; retards: number };
}
interface EtapeMES {
  cle: string; titre: string; fait: boolean; detail: string; ecran: string; bloquant: boolean;
}
interface MiseEnService { termine: boolean; faites: number; total: number; etapes: EtapeMES[] }

interface Bord {
  flotte: { tauxUtilisation: number; revPACMinor: number; prixMoyenJourMinor: number; chiffreAffairesMinor: number; diagnostic: string };
  tresorerie: Tresorerie;
  capacite: LigneCapacite[];
  alertes: { niveau: string; texte: string }[];
  demandesRefusees: { nombre: number; valeurPerdueMinor: number };
}

export default function Taches() {
  const { user, peut, estAgent } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState<Reponse | null>(null);
  const [bord, setBord] = useState<Bord | null>(null);
  const [detailTreso, setDetailTreso] = useState(false);
  const [mes, setMes] = useState<MiseEnService | null>(null);
  const [mesReplie, setMesReplie] = useState(false);

  useEffect(() => {
    api.get<Reponse>('/taches/aujourdhui').then(setD).catch(() => {});
    if (peut('voir.analytique')) api.get<Bord>('/analytique/tableau-de-bord').then(setBord).catch(() => {});
    // La liste de demarrage disparait d'elle-meme quand tout est fait.
    if (peut('voir.analytique')) api.get<MiseEnService>('/taches/mise-en-service').then(setMes).catch(() => {});
  }, [peut]);

  if (!d) return <Squelette lignes={6} />;
  const X = T.taches;

  async function ouvrirDossier(r: Reservation, sens: 'depart' | 'retour') {
    const c = await api.post<{ _id: string }>('/contrats', { reservation: r._id });
    nav(`/contrats/${c._id}/etat-des-lieux/${sens}`);
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{estAgent ? X.aFaireMaintenant : X.aujourdHui}</h1>
          <p>{new Date(d.date).toLocaleDateString(X.localeDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · {X.bonjour(user?.nom ?? '')}</p>
        </div>
        {!estAgent && (
          <div className="actions">
            <Link className="btn" to="/flotte">{X.mesVoitures}</Link>
            <Link className="btn" to="/disponibilites">{X.voituresLibres}</Link>
            <Link className="btn btn-p" to="/reservations/nouvelle">{X.nouvelleLocation}</Link>
          </div>
        )}
      </div>

      {/*
        * Le jour ou l'agence ouvre le logiciel, la base est vide et rien ne dit
        * par quoi commencer. Cette carte le dit, chiffre a l'appui, et s'efface
        * toute seule des que la mise en service est terminee.
        */}
      {mes && !mes.termine && (
        <div style={{ marginBottom: 18 }}>
          <Carte titre={X.miseEnService(mes.faites, mes.total)}
                 action={<button className="btn btn-s" onClick={() => setMesReplie(!mesReplie)}>
                   {mesReplie ? X.voirListe : X.reduire}
                 </button>}>
            <p className="mini sourdine" style={{ marginTop: -4 }}>
              {X.mesIntro}
            </p>
            {!mesReplie && (
              <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                {mes.etapes.map((e) => (
                  <div key={e.cle} style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                                            opacity: e.fait ? 0.55 : 1 }}>
                    <span style={{ fontSize: 16, lineHeight: '20px',
                                   color: e.fait ? 'var(--succes)' : e.bloquant ? 'var(--danger)' : 'var(--attention)' }}>
                      {e.fait ? '✓' : '○'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>
                        {e.titre}
                        {!e.fait && e.bloquant && <span className="badge refusee" style={{ marginLeft: 8 }}>{X.indispensable}</span>}
                      </div>
                      <div className="mini sourdine">{e.detail}</div>
                    </div>
                    {!e.fait && <Link className="btn btn-s" to={e.ecran}>{X.yAller}</Link>}
                  </div>
                ))}
              </div>
            )}
          </Carte>
        </div>
      )}

      {/* ---- Mode Pilotage : le chiffre qui justifie le logiciel ---- */}
      {bord && (
        <div className="grille" style={{ marginBottom: 18 }}>
          <div className="carte carte-p signature-kpi">
            <div className="micro">{X.tresoDisponible}</div>
            <div className="v">{montant(bord.tresorerie.disponibleMinor)}</div>
            <div className="decomposition">
              <span>{X.soldeCaisse}<b>{montant(bord.tresorerie.soldeCaisseMinor)}</b></span>
              <span>{X.moinsPrestations}<b>{montant(bord.tresorerie.prestationsFuturesEncaisseesMinor)}</b></span>
              <span>{X.moinsCautions}<b>{montant(bord.tresorerie.cautionsDetenuesMinor)}</b></span>
            </div>
            <button className="btn btn-s" style={{ marginTop: 12 }} onClick={() => setDetailTreso(true)}>{X.voirDetail}</button>
          </div>

          <div className="grille g4">
            <Kpi to="/flotte" libelle={X.tauxUtilisation} valeur={`${bord.flotte.tauxUtilisation} %`} detail={X.tauxDetail} />
            <Kpi to="/analytique" libelle={X.revenuParVoiture} valeur={montantCourt(bord.flotte.revPACMinor)} detail={X.revenuDetail} />
            <Kpi to="/analytique" libelle={X.prixMoyen} valeur={montantCourt(bord.flotte.prixMoyenJourMinor)} detail={X.prixDetail} />
            <Kpi to="/capacite" libelle={X.caMois} valeur={montantCourt(bord.flotte.chiffreAffairesMinor)} detail={X.caDetail} />
          </div>

          <Bandeau type="info" titre={X.diagnostic}>{bord.flotte.diagnostic}</Bandeau>

          {bord.demandesRefusees.nombre > 0 && (
            <Link to="/analytique" style={{ color: 'inherit' }}>
              <Bandeau type="accent" titre={X.argentInvisible}>
                {X.demandesRefusees(bord.demandesRefusees.nombre)}
                <b>{montant(bord.demandesRefusees.valeurPerdueMinor)}</b>{X.caPerdu}
              </Bandeau>
            </Link>
          )}
          {bord.alertes.slice(0, 2).map((a, i) => (
            <Bandeau key={i} type={a.niveau === 'attention' ? 'warn' : 'info'}>{a.texte}</Bandeau>
          ))}
        </div>
      )}

      {/* ---- Le rail : une file, un seul bouton par ligne ---- */}
      {d.resume.retards > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Bandeau type="ko" titre={X.enRetard(d.resume.retards)}>
            {estAgent ? X.signalezLes : X.enPriorite}
          </Bandeau>
        </div>
      )}

      <Carte titre={X.fileDuJour(d.resume.departs, d.resume.retours)} pad={false}>
        <div className="carte-p">
          {d.taches.length === 0 ? (
            <Vide titre={X.toutAJour}>{X.aucunDepart}</Vide>
          ) : (
            <div className="file">
              {d.taches.map((t) => {
                const v = t.reservation.vehicle as { marque: string; modele: string; immatriculation: string };
                return (
                  <div key={t.reservation._id + t.type} className={`tache ${t.type}`}>
                    <div className="heure">{heureFr(t.heure)}</div>
                    <div className="corps">
                      <div className="micro" style={{ color: t.type === 'retard' ? 'var(--danger)' : undefined }}>{t.titre}</div>
                      <div className="qui">
                        <ClientTel nom={nomClient(t.reservation.customer as never)}
                                   telephone={(t.reservation.customer as { telephone?: string })?.telephone} compact />
                      </div>
                      <div className="quoi">{v?.marque} {v?.modele} · {v?.immatriculation} · {t.reservation.numero}</div>
                    </div>
                    {t.action === 'signaler_responsable' ? (
                      <Link className="btn btn-d" to={`/reservations/${t.reservation._id}`}>{X.signaler}</Link>
                    ) : (
                      <button className="btn btn-p btn-l" onClick={() => void ouvrirDossier(t.reservation, t.action === 'faire_depart' ? 'depart' : 'retour')}>
                        {t.action === 'faire_depart' ? X.faireDepart : X.faireRetour}
                      </button>
                    )}
                  </div>
                );
              })}

              {d.correctionsEnAttente.map((c) => (
                <div key={c._id} className="tache gelee">
                  <div className="heure" style={{ fontSize: 14 }}>—</div>
                  <div className="corps">
                    <div className="micro" style={{ color: 'var(--attention)' }}>{X.attenteResponsable}</div>
                    <div className="qui">{X.champ(c.libelleEntite, c.champ)}</div>
                    <div className="quoi">{X.demandeLe(c.motif, dateFr(c.createdAt))}</div>
                  </div>
                  {peut('traiter.correction')
                    ? <Link className="btn" to="/corrections">{X.traiter}</Link>
                    : <span className="badge en_attente">{T.commun.statuts.en_attente}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </Carte>

      {d.encaissementsEnAttente.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Carte titre={X.soldesAEncaisser} pad={false}>
            <table className="tbl">
              <thead><tr><th>{X.thContrat}</th><th>{X.thClient}</th><th className="droite">{X.thSoldeDu}</th><th /></tr></thead>
              <tbody>
                {d.encaissementsEnAttente.map((c) => (
                  <tr key={c._id}>
                    <td><b>{c.numero}</b></td>
                    <td><ClientTel nom={nomClient(c.customer as never)}
                                   telephone={(c.customer as { telephone?: string })?.telephone} /></td>
                    <td className="droite montant">{montant(c.soldeMinor)}</td>
                    <td className="droite"><Link className="btn btn-s" to={`/caisse?contrat=${c._id}`}>{X.encaisser}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Carte>
        </div>
      )}

      {d.alertes.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Carte titre={X.aSurveiller}>
            <div style={{ display: 'grid', gap: 8 }}>
              {d.alertes.map((a, i) => <Bandeau key={i} type={a.niveau === 'danger' ? 'ko' : 'warn'}>{a.texte}</Bandeau>)}
            </div>
          </Carte>
        </div>
      )}

      {detailTreso && bord && (
        <Panneau titre={X.pasAVous} onFermer={() => setDetailTreso(false)}>
          <p className="sourdine mini" style={{ marginBottom: 14 }}>
            {X.pasAVousTexte}
          </p>
          <Carte titre={X.prestationsFutures} pad={false}>
            <table className="tbl compact">
              <thead><tr><th>{X.thLocation}</th><th>{X.thClient}</th><th>{X.thPeriode}</th><th className="droite">{X.thNonLivre}</th></tr></thead>
              <tbody>
                {bord.tresorerie.detailPrestations.map((p) => (
                  <tr key={p.numero}>
                    <td>{p.numero}</td><td>{p.client}</td>
                    <td className="mini sourdine">{dateFr(p.dateDebut)} → {dateFr(p.dateFin)}</td>
                    <td className="droite montant">{montant(p.nonLivreMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Carte>
          <div style={{ marginTop: 14 }}>
            <Bandeau type="warn" titre={X.cautionsDetenues}>
              {montant(bord.tresorerie.cautionsDetenuesMinor)}{X.cautionsTexte}
            </Bandeau>
          </div>
        </Panneau>
      )}
    </>
  );
}
